/**
 * Agent spotlight — one red element that IS the star and the ring. `spotlightForPatch` names the
 * element a patch should point at (by `data-spot`, looked up in the DOM rather than prop-threaded,
 * since targets are scattered across lever cards, year tabs and the capacity table). The overlay
 * below launches a compact star from the last agent message, blooms it into the framing ring on
 * each target, and collapses it back into the message when the turn ends.
 */

import { Sparkles } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import type { ScenarioPatch, ScenarioStatePayload } from "./stream";

export interface SpotlightTarget {
  key: string;
  label: string;
}

/** Ids present in exactly one of the two lists — for an excluded-family patch, the one that just
 *  toggled. */
function symmetricDiff(a: readonly string[], b: readonly string[]): string[] {
  const setA = new Set(a);
  const setB = new Set(b);
  return [...a.filter((x) => !setB.has(x)), ...b.filter((x) => !setA.has(x))];
}

const LEVER_NAME: Record<string, string> = {
  relo: "relocation",
  pack: "pack rationalisation",
  conv: "Rx→Dx conversion",
  cap: "capacity",
  pace: "NPI pacing",
};

/** The single element a just-applied patch should slide the ring to. */
export function spotlightForPatch(
  patch: ScenarioPatch,
  prev: ScenarioStatePayload,
): SpotlightTarget {
  if (patch.year !== undefined) {
    return { key: `year-${patch.year}`, label: `setting the working year to ${patch.year}` };
  }
  if (patch.relo_threshold !== undefined) {
    return {
      key: "lever-relo",
      label:
        patch.relo_threshold < 0
          ? "switching off relocation"
          : `setting the relocate threshold to ${patch.relo_threshold}`,
    };
  }
  if (patch.relo_excluded !== undefined) {
    const changed = symmetricDiff(prev.relo_excluded, patch.relo_excluded)[0];
    return {
      key: "lever-relo",
      label: changed
        ? `adjusting excluded relocation family ${changed}`
        : "adjusting the excluded relocation families",
    };
  }
  if (patch.pack_count !== undefined) {
    return { key: "lever-pack", label: `setting pack rationalisation to ${patch.pack_count}` };
  }
  if (patch.pack_excluded !== undefined) {
    const changed = symmetricDiff(prev.pack_excluded, patch.pack_excluded)[0];
    return {
      key: "lever-pack",
      label: changed
        ? `adjusting excluded pack family ${changed}`
        : "adjusting the excluded pack families",
    };
  }
  if (patch.conv_count !== undefined) {
    return { key: "lever-conv", label: `setting Rx→Dx conversion to ${patch.conv_count}` };
  }
  if (patch.conv_excluded !== undefined) {
    const changed = symmetricDiff(prev.conv_excluded, patch.conv_excluded)[0];
    return {
      key: "lever-conv",
      label: changed
        ? `adjusting excluded fitting set ${changed}`
        : "adjusting the excluded fitting sets",
    };
  }
  if (patch.cap_system !== undefined) {
    return { key: "lever-cap", label: `choosing ${LEVER_NAME.cap} system ${patch.cap_system}` };
  }
  if (patch.cap_units !== undefined) {
    return { key: "lever-cap", label: "dialling the capacity units" };
  }
  if (patch.pace_pct !== undefined) {
    return { key: "lever-pace", label: `pacing ${patch.pace_pct}% of the NPI intake` };
  }
  return { key: "hero", label: "updating the scenario" };
}

/** `prev` with the patch's set fields merged in — so the NEXT patch in the same turn diffs
 *  against the right prior state. */
export function mergeScenario(
  prev: ScenarioStatePayload,
  patch: ScenarioPatch,
): ScenarioStatePayload {
  return {
    year: patch.year ?? prev.year,
    relo_threshold: patch.relo_threshold ?? prev.relo_threshold,
    relo_excluded: patch.relo_excluded ?? prev.relo_excluded,
    pack_count: patch.pack_count ?? prev.pack_count,
    pack_excluded: patch.pack_excluded ?? prev.pack_excluded,
    conv_count: patch.conv_count ?? prev.conv_count,
    conv_excluded: patch.conv_excluded ?? prev.conv_excluded,
    cap_system: patch.cap_system ?? prev.cap_system,
    cap_units: patch.cap_units ?? prev.cap_units,
    pace_pct: patch.pace_pct ?? prev.pace_pct,
    agent_set: patch.agent_set ?? prev.agent_set,
  };
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** How far outside the target element the ring sits, in px, so it frames rather than traces it. */
const RING_MARGIN = 5;
/** The compact star's box size, in px. */
const STAR = 22;
/** `data-spot` of the last agent message — where the star launches from and returns into. */
const HOME = "agent-home";
/** How long the star spends gliding to/from the chat, in ms. */
const TRAVEL_MS = 480;

interface Point {
  x: number;
  y: number;
}

/** The one morphing element's current geometry + form: `star` while travelling to/from the chat,
 *  `ring` while framing a target. `animate:false` snaps (fresh spawn / layout reflow). */
interface Spot {
  x: number;
  y: number;
  w: number;
  h: number;
  /** For the birth/death morph: 0 = folded into the message, 1 = full size. */
  scale: number;
  opacity: number;
  mode: "star" | "ring";
  label: string;
  shown: boolean;
  animate: boolean;
}

/** The ring's box (with margin) for a `data-spot` element, in the container's own coordinates. */
function rectOf(container: HTMLElement, key: string): Box | null {
  const el = container.querySelector<HTMLElement>(`[data-spot="${key}"]`);
  if (!el) {
    return null;
  }
  const cr = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return {
    left: r.left - cr.left + container.scrollLeft - RING_MARGIN,
    top: r.top - cr.top + container.scrollTop - RING_MARGIN,
    width: r.width + RING_MARGIN * 2,
    height: r.height + RING_MARGIN * 2,
  };
}

/** Centre of a `data-spot` element, in the container's own coordinates. */
function centerOf(container: HTMLElement, key: string): Point | null {
  const el = container.querySelector<HTMLElement>(`[data-spot="${key}"]`);
  if (!el) {
    return null;
  }
  const cr = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return {
    x: r.left - cr.left + container.scrollLeft + r.width / 2,
    y: r.top - cr.top + container.scrollTop + r.height / 2,
  };
}

/**
 * The spotlight — ONE red element that IS the star and the ring. It launches from the last agent
 * message as a compact star, glides into the playground and BLOOMS into the framing ring on the
 * first element the agent touches, glides + resizes ring-to-ring as it visits each subsequent
 * one, then collapses back into a star that flies into the message and vanishes when the turn
 * ends. Measured against `.body` (see `index.tsx`), so it reaches the levers, the centre
 * sections, and the chat alike.
 */
export function SpotlightOverlay({
  containerRef,
  target,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  target: SpotlightTarget | null;
}) {
  const [spot, setSpot] = useState<Spot>({
    x: 0,
    y: 0,
    w: STAR,
    h: STAR,
    scale: 1,
    opacity: 1,
    mode: "star",
    label: "",
    shown: false,
    animate: false,
  });
  const spotRef = useRef(spot);
  const shownRef = useRef(false);
  const timers = useRef<number[]>([]);

  const apply = (next: Spot) => {
    spotRef.current = next;
    setSpot(next);
  };
  // Full-size star at a point.
  const star = (cx: number, cy: number, animate: boolean, label?: string): Spot => ({
    x: cx - STAR / 2,
    y: cy - STAR / 2,
    w: STAR,
    h: STAR,
    scale: 1,
    opacity: 1,
    mode: "star",
    label: label ?? spotRef.current.label,
    shown: true,
    animate,
  });
  // A star scaled/faded to nothing at a point — the "folded into the message" birth/death state.
  const seed = (cx: number, cy: number, animate: boolean, label?: string): Spot => ({
    ...star(cx, cy, animate, label),
    scale: 0,
    opacity: 0,
  });
  const ring = (b: Box, label: string, animate = true): Spot => ({
    x: b.left,
    y: b.top,
    w: b.width,
    h: b.height,
    scale: 1,
    opacity: 1,
    mode: "ring",
    label,
    shown: true,
    animate,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
    let raf = 0;
    const push = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };

    // Turn over — collapse the ring into a star where it stands, fly it to the message, and let it
    // shrink/fade INTO the bubble before it's gone (no abrupt disappear).
    if (!target) {
      if (!shownRef.current) {
        return;
      }
      const cx = spotRef.current.x + spotRef.current.w / 2;
      const cy = spotRef.current.y + spotRef.current.h / 2;
      apply(star(cx, cy, true));
      const home = centerOf(container, HOME);
      if (home) {
        push(() => apply(star(home.x, home.y, true)), 260);
        push(() => apply(seed(home.x, home.y, true)), 260 + TRAVEL_MS);
      }
      push(
        () => {
          shownRef.current = false;
          apply({ ...spotRef.current, shown: false, animate: false });
        },
        260 + TRAVEL_MS + 360,
      );
      return;
    }

    const box = rectOf(container, target.key);
    if (!box) {
      // Target isn't in the DOM — leave the element wherever it is rather than jump.
      return;
    }

    if (!shownRef.current) {
      // Take off: start folded inside the message (a zero-size seed), then grow OUT of the bubble
      // as it glides to the first target and blooms into the ring.
      shownRef.current = true;
      const tc = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
      const home = centerOf(container, HOME) ?? tc;
      apply(seed(home.x, home.y, false));
      raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          apply(star(tc.x, tc.y, true, target.label));
          push(() => apply(ring(box, target.label)), TRAVEL_MS);
        }),
      );
    } else {
      // Already in the playground — glide + resize the ring to the next target (the past
      // contraction/expansion between elements).
      apply(ring(box, target.label));
    }

    const onReflow = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const b = rectOf(container, target.key);
        if (b && shownRef.current && spotRef.current.mode === "ring") {
          apply(ring(b, target.label, false));
        }
      });
    };
    window.addEventListener("resize", onReflow);
    container.addEventListener("scroll", onReflow, { passive: true });
    return () => {
      window.removeEventListener("resize", onReflow);
      container.removeEventListener("scroll", onReflow);
      cancelAnimationFrame(raf);
    };
  }, [containerRef, target?.key]);

  if (!spot.shown) {
    return null;
  }
  return (
    <div
      className={cn("agent-spot", spot.mode, !spot.animate && "no-anim")}
      style={{
        transform: `translate(${spot.x}px, ${spot.y}px) scale(${spot.scale})`,
        width: spot.w,
        height: spot.h,
        opacity: spot.opacity,
      }}
    >
      <span className="agent-spot-ic">
        <Sparkles aria-hidden />
      </span>
      <span className="agent-spot-chip">
        <Sparkles aria-hidden />
        Agent {spot.label}
      </span>
    </div>
  );
}
